import { type AdminUserDto } from '../../services/admin.api';
import { brandIcon, icon } from '../../icons';
import { i18nService, t } from '../../services/i18n';

export type AdminBooleanFilter = '' | 'true' | 'false';

export function esc(str: string): string {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

export function escAttr(str: string): string {
  return esc(str).replace(/"/g, '&quot;');
}

export function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('nl-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Brussels',
    });
  } catch {
    return iso;
  }
}

export function formatCurrencyFromCents(cents: number): string {
  return i18nService.formatCurrency(cents / 100);
}

export function renderRoleBadge(role: AdminUserDto['role']): string {
  const isAdmin = role === 1 || String(role).toLowerCase() === 'admin';
  return isAdmin
    ? `<span class="badge badge-admin">${t('admin.role.admin')}</span>`
    : `<span class="badge badge-landlord">${t('admin.role.landlord')}</span>`;
}

export function renderBlockedBadge(isBlocked: boolean): string {
  return isBlocked
    ? `<span class="badge badge-danger">${t('admin.blocked')}</span>`
    : `<span class="badge badge-landlord">${t('admin.active')}</span>`;
}

export function renderRow(user: AdminUserDto, index: number, isSelected: boolean, isSelectable: boolean): string {
  const providerIcon = user.provider === 'google'
    ? brandIcon('google', { size: 18, className: 'admin-provider-icon' })
    : user.provider === 'microsoft'
      ? brandIcon('microsoft', { size: 18, className: 'admin-provider-icon' })
      : icon('mail', { size: 18, className: 'admin-provider-icon' });
  const createdAt = formatDate(user.createdAt);
  const lastLoggedIn = user.lastLoggedIn ? formatDate(user.lastLoggedIn) : '&mdash;';
  const onboarding = user.onboardingCompleted
    ? `<span class="admin-check admin-check--yes" title="${t('admin.completed')}">&#10003;</span>`
    : `<span class="admin-check admin-check--no" title="${t('admin.notCompleted')}">&mdash;</span>`;
  const business = user.isBusinessAccount
    ? `<span class="admin-check admin-check--yes" title="${t('admin.yes')}">&#10003;</span>`
    : `<span class="admin-check admin-check--no" title="${t('admin.no')}">&mdash;</span>`;
  const blocked = renderBlockedBadge(user.isBlocked);
  const roleLabel = renderRoleBadge(user.role);

  return `
    <tr>
      <td class="admin-center">
        <input
          type="checkbox"
          data-action="toggle-user-selection"
          data-user-id="${escAttr(user.id)}"
          ${isSelected ? 'checked' : ''}
          ${isSelectable ? '' : 'disabled'}
        >
      </td>
      <td class="admin-provider-cell">${providerIcon}</td>
      <td class="admin-cell-name">${esc(user.effectiveDisplayName)}</td>
      <td class="admin-cell-email">${esc(user.email)}</td>
      <td class="admin-cell-date">${createdAt}</td>
      <td class="admin-cell-date">${lastLoggedIn}</td>
      <td class="admin-cell-role">${blocked}</td>
      <td class="admin-center">${onboarding}</td>
      <td class="admin-center">${business}</td>
      <td class="admin-cell-role">${roleLabel}</td>
      <td class="admin-center">
        <button class="admin-eye-btn" data-index="${index}" title="${t('admin.viewDetails')}">
          ${icon('eye', { size: 15 })}
        </button>
      </td>
    </tr>
  `;
}

export function renderBooleanOptions(selected: AdminBooleanFilter, type: 'business' | 'onboarding'): string {
  const options = type === 'business'
    ? [
      { value: '', label: t('admin.filter.all') },
      { value: 'true', label: t('admin.yes') },
      { value: 'false', label: t('admin.no') },
    ]
    : [
      { value: '', label: t('admin.filter.all') },
      { value: 'true', label: t('admin.completed') },
      { value: 'false', label: t('admin.notCompleted') },
    ];

  return options.map((option) =>
    `<option value="${option.value}" ${selected === option.value ? 'selected' : ''}>${option.label}</option>`).join('');
}

export function parseBooleanFilter(value: AdminBooleanFilter): boolean | undefined {
  if (value === '') return undefined;
  return value === 'true';
}

export function normalizeBooleanFilter(value: FormDataEntryValue | null): AdminBooleanFilter {
  return value === 'true' || value === 'false' ? value : '';
}
